import type { Calculation, ResourceNode, ResourceResponse, TimeSeriesData } from './types';

export class ContentInputError extends Error {
  constructor(readonly code: 'badCsv' | 'badInputs' | 'badEvents' | 'fileFailed') {
    super(code);
  }
}

/** Import deliberately uses two columns, matching the exported/manual editing format. */
export function parseSeries(text: string, unit: string, frequency: TimeSeriesData['frequency']): TimeSeriesData {
  const rows = text.trim().split(/\r?\n/).filter((line) => line.trim() !== '');
  if (rows[0]?.trim().toLowerCase() === 'period,value') rows.shift();
  const seen = new Set<string>();
  const points = rows.map((line) => {
    const columns = line.split(',').map((cell) => cell.trim().replace(/^"(.*)"$/, '$1'));
    const period = columns[0]!;
    const raw = columns[1] ?? '';
    const value = raw === '' ? null : Number(raw);
    if (columns.length !== 2 || period === '' || seen.has(period) || (value !== null && !Number.isFinite(value))) {
      throw new ContentInputError('badCsv');
    }
    seen.add(period);
    return { period, value };
  });
  return { unit, frequency, points };
}

export function formatSeries(series: TimeSeriesData | null): string {
  return ['period,value', ...(series?.points.map(({ period, value }) => `${period},${value ?? ''}`) ?? [])].join('\n');
}

export function parseInputs(text: string): Calculation['inputs'] {
  const inputs: Calculation['inputs'] = {};
  for (const row of text.split(/\r?\n/).filter((line) => line.trim() !== '')) {
    const match = /^\s*([A-Za-z][A-Za-z0-9_]*)\s*=\s*(urn:\S+)\s*$/.exec(row);
    if (match === null || Object.hasOwn(inputs, match[1]!)) throw new ContentInputError('badInputs');
    inputs[match[1]!] = match[2]!;
  }
  return inputs;
}

export function formatInputs(calculation: Calculation | null): string {
  return Object.entries(calculation?.inputs ?? {}).map(([name, urn]) => `${name} = ${urn}`).join('\n');
}

export interface CalendarEvent { date: string; title: string }

export function parseEvents(text: string): CalendarEvent[] {
  return text.split(/\r?\n/).filter((line) => line.trim() !== '').map((line) => {
    const match = /^(\d{4}-\d{2}-\d{2}),\s*(.+)$/.exec(line.trim());
    if (match === null || Number.isNaN(Date.parse(match[1]!)) || new Date(match[1]!).toISOString().slice(0, 10) !== match[1]) {
      throw new ContentInputError('badEvents');
    }
    return { date: match[1]!, title: match[2]! };
  });
}

/** The server validates properties; old/custom calendar payloads still render safely. */
export function calendarEvents(resource: ResourceResponse): CalendarEvent[] {
  const raw = resource.properties['events'];
  if (raw === undefined) return [];
  try {
    const rows: unknown = JSON.parse(raw);
    if (!Array.isArray(rows)) return [];
    return rows.filter((row): row is CalendarEvent => typeof row === 'object' && row !== null && typeof row.date === 'string' && typeof row.title === 'string');
  } catch {
    return [];
  }
}

/** A private child boundary is omitted from the publication's contained tree. */
export function publicationContents(nodes: ResourceNode[], parentVisible = true): { name: string; visible: boolean }[] {
  return nodes.flatMap((node) => {
    const visible = parentVisible && node.resource.public !== 'private';
    return [{ name: node.resource.name, visible }, ...publicationContents(node.children, visible)];
  });
}

/** Wire timestamps stay UTC; human-facing times use the product's Norwegian time zone. */
export function formatResourceTime(timestamp: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'long', timeZone: 'Europe/Oslo' }).format(new Date(timestamp));
}
